import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  ConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from '@/components';
import { Column } from '@/types/common';
import { useAuth } from '@/contexts/AuthContext';
import {
  getShipments,
  getShipmentSummary,
  getShipmentDetail,
  getBulkShipmentItems,
  shipmentToPackageRows,
  cancelShipment,
  isShipmentCancellable,
  Shipment,
  ShipmentListParams,
  ShipmentSummary,
  ShipmentSummaryParams,
  ShipmentDetail,
  ImportCommitResult,
} from '@/services/api/shipments';
import { PackageRow } from '@/services/api/packages';
import { ShipmentStatusBadge } from './ShipmentStatusBadge';
import ShipmentFilter, {
  ShipmentFilterValue,
  EMPTY_FILTER,
} from './ShipmentFilter';
import ShipmentDetailDrawer from './ShipmentDetailDrawer';
import { ShipmentEximBadge } from './ShipmentEximBadge';
import ExportShipmentsModal from './ExportShipmentsModal';
import ImportModal from './ImportModal';
import CreateShipmentModal from './CreateShipmentModal';
import BulkPrintShipmentsModal from './BulkPrintShipmentsModal';

// Lazy so the react-pdf bundle only loads when a user actually prints.
const LabelPreviewModal = lazy(() => import('@/components/LabelPreviewModal'));

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

// Cap on shipments per bulk print — each one is fetched + expands to N labels,
// so a large selection means a heavy PDF. Tune with the team if needed.
const MAX_BULK_PRINT_SHIPMENTS = 50;

// Only shipments still in a printable stage can be bulk-selected for labels:
// CREATED (not yet at the hub), AT_HUB (scanned in, not dispatched yet) or
// (PARTIALLY_)DISPATCHED. Anything further along (IN_DELIVERY, COMPLETED,
// IN_DISPUTE, RETURNED, FAILED) or CANCELLED is not selectable.
const isBulkPrintable = (status: Shipment['status']) =>
  status === 'CREATED' ||
  status === 'AT_HUB' ||
  status === 'DISPATCHED' ||
  status === 'PARTIALLY_DISPATCHED';

// Ringkasan cards — mirrors the invoices summary pattern (SummaryCardDef +
// `count` reader + clickable drill-in). `status: null` is the Total card and
// clears the status filter. Every status is shown, so Total === sum of the
// status cards. One semantic colour per card for at-a-glance scanning.
interface SummaryCardDef {
  id: string;
  label: string;
  dot: string; // status dot colour (DS semantic token)
  ring: string; // active ring colour
  status: string | null; // null = Total (clears the status filter)
  count: (s: ShipmentSummary) => number | undefined;
}

const SUMMARY_CARDS: SummaryCardDef[] = [
  {
    id: 'total',
    label: 'Total shipment',
    dot: 'bg-primary',
    ring: 'ring-primary',
    status: null,
    count: (s) => s.total,
  },
  {
    id: 'created',
    label: 'Created',
    dot: 'bg-information-base',
    ring: 'ring-information-base',
    status: 'CREATED',
    count: (s) => s.created,
  },
  {
    id: 'at_hub',
    label: 'At Hub',
    dot: 'bg-highlighted-base',
    ring: 'ring-highlighted-base',
    status: 'AT_HUB',
    count: (s) => s.atHub,
  },
  {
    id: 'partially',
    label: 'Partially Dispatched',
    dot: 'bg-away-base',
    ring: 'ring-away-base',
    status: 'PARTIALLY_DISPATCHED',
    count: (s) => s.partiallyDispatched,
  },
  {
    id: 'dispatched',
    label: 'Dispatched',
    dot: 'bg-away-base',
    ring: 'ring-away-base',
    status: 'DISPATCHED',
    count: (s) => s.dispatched,
  },
  {
    id: 'in_delivery',
    label: 'In Delivery',
    dot: 'bg-primary',
    ring: 'ring-primary',
    status: 'IN_DELIVERY',
    count: (s) => s.inDelivery,
  },
  {
    id: 'completed',
    label: 'Completed',
    dot: 'bg-success-base',
    ring: 'ring-success-base',
    status: 'COMPLETED',
    count: (s) => s.completed,
  },
  {
    id: 'in_dispute',
    label: 'In Dispute',
    dot: 'bg-warning-dark',
    ring: 'text-warning-dark',
    status: 'IN_DISPUTE',
    count: (s) => s.inDispute,
  },
  {
    id: 'returned',
    label: 'Returned',
    dot: 'bg-warning-base',
    ring: 'ring-warning-base',
    status: 'RETURNED',
    count: (s) => s.returned,
  },
  {
    id: 'failed',
    label: 'Failed',
    dot: 'bg-error-base',
    ring: 'ring-error-base',
    status: 'FAILED',
    count: (s) => s.failed,
  },
  {
    id: 'cancelled',
    label: 'Cancelled',
    dot: 'bg-faded-base',
    ring: 'ring-faded-base',
    status: 'CANCELLED',
    count: (s) => s.cancelled,
  },
];

const ShipmentsPage: React.FC = () => {
  // The authenticated client (company); its `id` scopes the shipments list.
  // Already fetched by AuthContext on login/reload — no extra call needed.
  const { user, client } = useAuth();
  // Provider-user tokens have no email; the cancel is attributed to the
  // logged-in user so the BE doesn't record "system".
  const actor =
    user?.email || user?.displayName || user?.username || undefined;
  const clientId = client?.id;

  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ShipmentSummary>({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ShipmentFilterValue>(EMPTY_FILTER);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // Bulk label print: selected shipment ids (current view only — cleared when
  // the filter/page changes so there are no invisible "ghost" selections).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPrinting, setBulkPrinting] = useState(false);
  // Shipments (with items) shown in the bulk-print review modal; null = closed.
  const [bulkDetails, setBulkDetails] = useState<ShipmentDetail[] | null>(null);

  // Single source of truth for the filter query so the list and the CSV export
  // always send the same BE params (only pagination differs). Empty values are
  // omitted so they don't reach the API as blank strings.
  const filterParams = useMemo<Omit<ShipmentListParams, 'page' | 'limit'>>(
    () => ({
      clientId,
      search: search || undefined,
      status: filters.status || undefined,
      exim: filters.exim || undefined,
      createdFrom: filters.startDate || undefined,
      createdTo: filters.endDate || undefined,
    }),
    [clientId, search, filters]
  );

  // Label printing: fetch the clicked shipment's items, map to PackageRows,
  // and open the shared LabelPreviewModal. `printingId` drives the per-row
  // spinner so a slow detail fetch doesn't look frozen.
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [labelPackages, setLabelPackages] = useState<PackageRow[] | null>(null);

  // Row action menu (3-dot): which row's dropdown is open, plus the
  // cancel-shipment confirmation target and its in-flight state.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Shipment | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Map a loaded shipment detail to label rows and open the modal. Shared by
  // the row Print button and the detail drawer's Print button.
  const openLabelsFromDetail = (detail: ShipmentDetail) => {
    const rows = shipmentToPackageRows(detail, client);
    if (rows.length === 0) {
      showErrorToast(
        'Tidak ada item',
        'Shipment ini belum punya item untuk dicetak.'
      );
      return;
    }
    // Close the detail drawer before opening the label modal — otherwise both
    // portals stack and overlap. No-op for the row Print path (no drawer open).
    setDetailId(null);
    setLabelPackages(rows);
  };

  // Row Print button: fetch the shipment's items first (the table row doesn't
  // carry them), then open the label modal.
  const handlePrint = async (shipment: Shipment) => {
    setPrintingId(shipment.id);
    try {
      openLabelsFromDetail(await getShipmentDetail(shipment.id));
    } catch (err) {
      showErrorToast(
        'Gagal memuat item',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setPrintingId(null);
    }
  };

  // Cancel a whole shipment (only allowed while CREATED). On success, refresh
  // the table + summary so the row flips to CANCELLED and the counts update.
  const handleCancelShipment = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelShipment(cancelTarget.id, { cancelledBy: actor });
      showSuccessToast(
        'Shipment dibatalkan',
        `${cancelTarget.waybill} telah dibatalkan.`
      );
      setCancelTarget(null);
      refreshAll();
    } catch (err) {
      showErrorToast(
        'Gagal membatalkan',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setCancelling(false);
    }
  };

  // Summary scope = the list filters minus `status`: the cards are the
  // per-status breakdown, so the selected status must not collapse them (and
  // toggling status shouldn't refetch the summary — the counts don't change).
  const summaryParams = useMemo<ShipmentSummaryParams>(
    () => ({
      clientId,
      search: search || undefined,
      createdFrom: filters.startDate || undefined,
      createdTo: filters.endDate || undefined,
    }),
    [clientId, search, filters.startDate, filters.endDate]
  );

  // getShipmentSummary never throws (returns zeros on error), so no try/catch —
  // mirrors the invoices summary. A failed summary leaves cards at 0, table intact.
  const fetchSummary = useCallback(async () => {
    setSummary(await getShipmentSummary(summaryParams));
  }, [summaryParams]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getShipments({
        ...filterParams,
        page,
        limit: pageSize,
      });
      setShipments(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (error) {
      showErrorToast(
        'Gagal memuat data',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
      setShipments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  // Refresh both the table and the summary — used by the Refresh button and
  // after a shipment is created/imported (filterParams didn't change, so the
  // summary effect won't re-run on its own).
  const refreshAll = useCallback(() => {
    fetchShipments();
    fetchSummary();
  }, [fetchShipments, fetchSummary]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleApplyFilters = (next: ShipmentFilterValue) => {
    setFilters(next);
    setPage(1);
  };

  // Clicking a summary card drills the table into that status (mirrors the
  // invoices cards). Clicking the active card, or Total, clears the filter.
  const handleCardClick = (card: SummaryCardDef) => {
    setFilters((f) => ({
      ...f,
      status: card.status && f.status !== card.status ? card.status : '',
    }));
    setPage(1);
  };

  const isCardActive = (card: SummaryCardDef) =>
    (filters.status || '') === (card.status || '');

  // ── Bulk label print ──────────────────────────────────────────────────
  // Only shipments still in a printable stage can be selected for bulk label
  // printing: CREATED (not yet dispatched) or DISPATCHED.
  const selectableShipments = shipments.filter((s) =>
    isBulkPrintable(s.status)
  );
  const allSelected =
    selectableShipments.length > 0 &&
    selectableShipments.every((s) => selectedIds.has(s.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = (checked: boolean) =>
    setSelectedIds(
      checked ? new Set(selectableShipments.map((s) => s.id)) : new Set()
    );

  // Selection is scoped to the current view — reset it whenever the query or
  // page changes so the count never includes rows the user can't see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterParams, page, pageSize]);

  // One round-trip for all selected shipments' items, then open the review
  // modal (waybill · booking id · receiver · items to print).
  const handleBulkPrint = async () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > MAX_BULK_PRINT_SHIPMENTS) {
      showErrorToast(
        'Terlalu banyak shipment',
        `Maksimum ${MAX_BULK_PRINT_SHIPMENTS} shipment per cetak. Pilih ${MAX_BULK_PRINT_SHIPMENTS} atau kurang.`
      );
      return;
    }
    setBulkPrinting(true);
    try {
      const details = await getBulkShipmentItems(Array.from(selectedIds));
      // Guard: nothing printable across the whole selection.
      if (details.every((d) => shipmentToPackageRows(d).length === 0)) {
        showErrorToast(
          'Tidak ada item',
          'Shipment terpilih belum punya item untuk dicetak.'
        );
        return;
      }
      setBulkDetails(details);
    } catch (err) {
      showErrorToast(
        'Gagal memuat item',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setBulkPrinting(false);
    }
  };

  const columns: Column<Shipment>[] = [
    {
      key: 'waybill',
      title: (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-stroke-soft-200 accent-primary"
            checked={allSelected}
            onChange={(e) => selectAll(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Pilih semua shipment"
          />
          <span>Waybill</span>
        </div>
      ),
      sticky: 'left',
      render: (_v, record) => (
        <div className="flex items-center gap-2">
          {isBulkPrintable(record.status) ? (
            <input
              type="checkbox"
              className="rounded border-stroke-soft-200 accent-primary flex-shrink-0"
              checked={selectedIds.has(record.id)}
              onChange={() => toggleSelect(record.id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Pilih ${record.waybill}`}
            />
          ) : (
            // Keep alignment without an actionable checkbox for rows whose
            // status isn't printable (e.g. IN_DELIVERY, COMPLETED, CANCELLED).
            <span className="inline-block h-4 w-4 flex-shrink-0" />
          )}
          <span className="font-mono text-xs font-semibold text-primary">
            {record.waybill}
          </span>
        </div>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {record.bookingId || '—'}
        </span>
      ),
    },
    {
      key: 'receiverName',
      title: 'Receiver',
      render: (_v, record) => (
        <span className="text-xs font-medium text-text-strong-950">
          {record.receiverName || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => <ShipmentStatusBadge status={record.status} />,
    },
    {
      // EXIM direction tag — Export / Import; Umum renders as a dash.
      key: 'exim',
      title: 'Tag',
      render: (_v, record) =>
        record.exim === 'EXPORT' || record.exim === 'IMPORT' ? (
          <ShipmentEximBadge exim={record.exim} />
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'itemCount',
      title: 'Items',
      align: 'center',
      render: (_v, record) => (
        <span className="text-text-strong-950">{record.itemCount ?? 0}</span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (_v, record) => (
        <span className="text-text-sub-600">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (_v, record) => {
        const menuActions: ActionDropdownAction[] = [
          {
            key: 'detail',
            label: 'Lihat detail',
            icon: 'ri-eye-line',
            onClick: () => {
              setOpenMenuId(null);
              setDetailId(record.id);
            },
          },
          {
            key: 'print',
            label: 'Print Shipment Label',
            icon: 'ri-printer-line',
            // Stays open (and spinning) while the items are fetched — the menu
            // item is the only feedback surface now that the row button is gone.
            isLoading: printingId === record.id,
            onClick: async () => {
              await handlePrint(record);
              setOpenMenuId(null);
            },
          },
          ...(isShipmentCancellable(record.status)
            ? [
                {
                  key: 'cancel',
                  label: 'Batalkan shipment',
                  icon: 'ri-close-circle-line',
                  // Important-modifier: ActionDropdown's base `text-text-sub-600`
                  // otherwise wins the specificity tie and greys this out. Red
                  // text + icon signals this is a destructive action.
                  className:
                    'text-error-base! hover:bg-error-lighter! hover:text-error-base!',
                  onClick: () => {
                    setOpenMenuId(null);
                    setCancelTarget(record);
                  },
                },
              ]
            : []),
        ];

        return (
          <div className="flex items-center justify-end">
            <ActionDropdown
              record={record}
              isOpen={openMenuId === record.id}
              onToggle={() =>
                setOpenMenuId((cur) => (cur === record.id ? null : record.id))
              }
              actions={menuActions}
              ariaLabel={`Aksi untuk ${record.waybill}`}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-4 space-y-5 sm:p-6">
      {/* Page head — title left, actions pinned top-right (as in the prototype).
          Stacks on mobile so the buttons stay reachable. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-bold text-primary">01</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
              Shipments
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-strong-950 sm:text-3xl">
            Shipments
          </h1>
          <p className="max-w-2xl text-sm text-text-sub-600">
            Lacak semua shipment masuk untuk akun Anda — isi paket, status, dan
            bukti per Waybill. Klik baris untuk membuka detail.
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-shrink-0 sm:gap-3">
          <Button
            variant="primary"
            onClick={handleBulkPrint}
            disabled={bulkPrinting || selectedIds.size === 0}
            className="flex-1 justify-center sm:flex-none"
          >
            {bulkPrinting ? (
              <>
                <i className="ri-loader-4-line mr-1.5 animate-spin" />
                Menyiapkan…
              </>
            ) : (
              <>
                <i className="ri-printer-line mr-1.5" />
                Print labels
                {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={refreshAll}
            className="flex-1 justify-center sm:flex-none"
          >
            <i className="ri-refresh-line mr-1.5" />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={() => setExportOpen(true)}
            className="flex-1 justify-center sm:flex-none"
          >
            <i className="ri-download-2-line mr-1.5" />
            Export CSV
          </Button>
          <Button
            variant="primary"
            onClick={() => setImportOpen(true)}
            className="flex-1 justify-center sm:flex-none"
          >
            <i className="ri-upload-2-line mr-1.5" />
            Import CSV
          </Button>
          {/* Create Shipment — temporarily hidden (feature paused). The modal +
              all wiring below stay intact; re-enable by un-commenting this
              button when the feature resumes. */}
          {/* <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            className="flex-1 justify-center sm:flex-none"
          >
            <i className="ri-add-line mr-1.5" />
            Create Shipment
          </Button> */}
        </div>
      </div>

      {/* Ringkasan — per-status summary cards, clickable to drill the table
          into that status (counts from the BE summary endpoint, scoped by the
          current search/date filters). */}
      <section>
        <div className="mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
            Ringkasan
          </span>
        </div>
        {/* 11 cards (total + the ten statuses) — 6 per row on wide screens so
            they land in two tidy rows. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {SUMMARY_CARDS.map((card) => {
            const active = isCardActive(card);
            const value = card.count(summary);
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => handleCardClick(card)}
                className={`flex flex-col gap-2 rounded-xl border bg-bg-white-0 p-4 text-left shadow-card-xs transition-all hover:shadow-card-sm ${
                  active
                    ? `border-transparent ring-2 ${card.ring}`
                    : 'border-stroke-soft-200 hover:border-stroke-sub-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${card.dot}`}
                  />
                  <span className="truncate text-xs font-medium text-text-sub-600">
                    {card.label}
                  </span>
                </div>
                <div className="text-2xl font-bold tracking-tight tabular-nums text-text-strong-950">
                  {value ?? 0}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Table card */}
      <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 p-4">
          <InputSearch
            className="w-full sm:min-w-[200px] sm:max-w-[420px] sm:flex-1"
            placeholder="Search waybill atau booking ID…"
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
          />
          <ShipmentFilter value={filters} onApply={handleApplyFilters} />
        </div>

        <TableData<Shipment>
          data={shipments}
          columns={columns}
          loading={loading}
          className="rounded-none border-0 shadow-none"
          onRowClick={(record) => setDetailId(record.id)}
          renderMobileCard={(record) => (
            <div className="flex items-start justify-between gap-3 p-4">
              {isBulkPrintable(record.status) && (
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-stroke-soft-200 accent-primary flex-shrink-0"
                  checked={selectedIds.has(record.id)}
                  onChange={() => toggleSelect(record.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Pilih ${record.waybill}`}
                />
              )}
              <div className="min-w-0 flex-1">
                <span className="block truncate font-mono text-sm font-semibold text-primary">
                  {record.waybill}
                </span>
                <span className="mt-1 block text-sm font-medium text-text-strong-950">
                  {record.receiverName || '—'}
                </span>
                <span className="mt-0.5 block font-mono text-[11px] text-text-soft-400">
                  Booking · {record.bookingId || '—'}
                </span>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-sub-600">
                  <span>
                    <i className="ri-box-3-line mr-1" />
                    {record.itemCount ?? 0} item
                  </span>
                  <span>
                    <i className="ri-time-line mr-1" />
                    {formatDateTime(record.createdAt)}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <ShipmentStatusBadge status={record.status} />
                <i className="ri-arrow-right-s-line text-lg text-text-soft-400" />
              </div>
            </div>
          )}
        />

        <div className="px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      </div>

      <ExportShipmentsModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        baseParams={filterParams}
      />

      <ShipmentDetailDrawer
        shipmentID={detailId}
        onClose={() => setDetailId(null)}
        onPrint={openLabelsFromDetail}
        onCancelled={refreshAll}
        clientName={client?.name}
        cancelledBy={actor}
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelShipment}
        title="Batalkan shipment ini?"
        message={
          cancelTarget
            ? `Shipment ${cancelTarget.waybill} beserta semua itemnya akan ditandai CANCELLED. Tindakan ini tidak bisa dibatalkan.`
            : ''
        }
        confirmLabel="Batalkan shipment"
        cancelLabel="Kembali"
        tone="danger"
        loading={cancelling}
      />

      {labelPackages && (
        <Suspense fallback={null}>
          <LabelPreviewModal
            open={labelPackages !== null}
            onClose={() => setLabelPackages(null)}
            packages={labelPackages}
          />
        </Suspense>
      )}

      {bulkDetails && (
        <BulkPrintShipmentsModal
          open={bulkDetails !== null}
          shipments={bulkDetails}
          onClose={() => setBulkDetails(null)}
        />
      )}

      {clientId != null && (
        <ImportModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          clientId={clientId}
          onImported={(_result: ImportCommitResult) => {
            setImportOpen(false);
            refreshAll();
          }}
        />
      )}

      {clientId != null && (
        <CreateShipmentModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          clientId={clientId}
          onCreated={(_result: ImportCommitResult) => {
            setCreateOpen(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
};

export default ShipmentsPage;
